/**
 * useEntityActions Hook
 * React hook for accessing entity actions with role-based filtering
 */

"use client";

import { useMemo } from "react";
import { getEntityActionsForContext } from "./getEntityActions";
import type { EntityKey, EntityRowType } from "./types/entity-keys";
import type { DetailActionStrict } from "./types/detail-action";
import type { ActionContextType } from "./types/action-names";

interface UseEntityActionsOptions<K extends EntityKey> {
  /** The context where actions will be used */
  context: ActionContextType;
  /** User's role for filtering actions */
  role?: string;
  /** The entity item (for visibility/disabled evaluation) */
  item?: EntityRowType<K>;
}

/**
 * Hook to get actions for an entity with optional filtering
 *
 * @example
 * const actions = useEntityActions('contacts', {
 *   context: 'detail-page',
 *   role: 'wtc_admin',
 *   item: contactData,
 * });
 */
export function useEntityActions<K extends EntityKey>(
  entity: K,
  options: UseEntityActionsOptions<K>
): DetailActionStrict<EntityRowType<K>>[] {
  return useMemo(() => {
    let actions = getEntityActionsForContext(entity, options.context);

    // Filter by role if provided
    const role = options.role;
    if (role) {
      actions = actions.filter((action) => {
        // If no role restriction, action is available to all
        if (!action.allowedRoles || action.allowedRoles.length === 0) {
          return true;
        }
        // Check if user's role is in the allowed roles
        return action.allowedRoles.includes(role);
      });
    }

    // Filter by visibility if item is provided
    const item = options.item;
    if (item !== undefined) {
      actions = actions.filter((action) => {
        // If no visibility predicate, action is always visible
        if (!action.visible) {
          return true;
        }
        // Evaluate visibility predicate
        return action.visible(item);
      });
    }

    return actions;
  }, [entity, options.context, options.role, options.item]);
}

export type { UseEntityActionsOptions };
